import React from "react";
import { useNavigate } from "react-router-dom";

import ViewContainer from "../../../../components/layout/view/ViewContainer";
import ContentPanel from "../../../../components/layout/content/ContentPanel";
import ContentPanelHeader from "../../../../components/layout/content/ContentPanelHeader";
import Text from "../../../../components/typography/Text";

import ContentGrid from "../../../../components/grids/content/ContentGrid";
import EmptyState from "../../../../components/feedback/EmptyState";
import Button from "../../../../components/buttons/Button";

import VideoThumbnailCard from "../../../../components/cards/thumbnails/VideoThumbnailCard";
import PopThumbnailCard from "../../../../components/cards/pops/PopThumbnailCard";
import AlbumThumbnailCard from "../../../../components/cards/album/AlbumCard";
import SharedTalentCard from "../../../talents/components/cards/SharedTalentCard";

import { useGlobalSearch } from "../hooks/useGlobalSearch";

import "./GlobalSearchView.css";

const SECTIONS = {
  videos: { title: "Videos", columns: 4 },
  pops: { title: "Pops", columns: 6 },
  albums: { title: "Albums", columns: 4 },
  streams: { title: "Streams", columns: 4 },
  talents: { title: "Talents", columns: 3 },
};

export default function GlobalSearchView({ type = "videos", search = "" }) {
  const navigate = useNavigate();

  const {
    results,
    total,
    hasMore,
    loading,
    loadingMore,
    error,
    hasSearch,
    validType,
    loadMore,
  } = useGlobalSearch(type, search);

  const section = SECTIONS[type];
  const resultItems = Array.isArray(results) ? results : [];
  const hasResults = resultItems.length > 0;

  const handleSelectContent = (item) => {
    const routes = {
      videos: item.videoTag
        ? `/watch/${encodeURIComponent(item.videoTag)}`
        : null,

      pops: item.popTag ? `/pops/${encodeURIComponent(item.popTag)}` : null,

      albums: item.albumTag
        ? `/albums/${encodeURIComponent(item.albumTag)}`
        : null,

      streams: item.streamSessionId
        ? `/streams/${encodeURIComponent(item.streamSessionId)}`
        : null,

      talents: item.talentTag
        ? `/talents/${encodeURIComponent(item.talentTag)}`
        : null,
    };

    const path = routes[type];

    if (path) navigate(path);
  };

  const renderResult = (item) => {
    const onClick = () => handleSelectContent(item);

    switch (type) {
      case "videos":
        return (
          <VideoThumbnailCard video={item} onClick={onClick} showPlayButton />
        );

      case "pops":
        return <PopThumbnailCard pop={item} onClick={onClick} />;

      case "albums":
        return <AlbumThumbnailCard album={item} onClick={onClick} />;

      case "streams":
        return (
          <VideoThumbnailCard
            video={{
              ...item,
              videoId: item.streamSessionId,
              videoTag: String(item.streamSessionId),
            }}
            onClick={onClick}
            showPlayButton
          />
        );

      case "talents":
        return <SharedTalentCard talent={item} />;

      default:
        return null;
    }
  };

  if (!validType) {
    return (
      <ViewContainer>
        <ContentPanel>
          <EmptyState
            title="Invalid category"
            description="Please select a valid search category."
          />
        </ContentPanel>
      </ViewContainer>
    );
  }

  if (!hasSearch) {
    return (
      <ViewContainer>
        <ContentPanel>
          <EmptyState
            title="Search Katch"
            description="Select a category and enter a search term."
          />
        </ContentPanel>
      </ViewContainer>
    );
  }

  return (
    <ViewContainer>
      <ContentPanel>
        <ContentPanelHeader
          title={`${section.title} (${total ?? resultItems.length})`}
        />

        {loading && !hasResults ? (
          <ContentGrid
            items={[]}
            loading
            columns={section.columns}
            tabletColumns={type === "pops" ? 4 : 3}
            mobileColumns={type === "talents" ? 1 : 2}
            gap={16}
            skeletonCount={12}
          />
        ) : !hasResults ? (
          <EmptyState
            title={error ? "Search unavailable" : "No results found"}
            description={
              error ||
              `No matching ${section.title.toLowerCase()} found for "${search}".`
            }
          />
        ) : (
          <>
            <ContentGrid
              items={resultItems}
              columns={section.columns}
              tabletColumns={type === "talents" ? 2 : type === "pops" ? 4 : 3}
              mobileColumns={type === "talents" ? 1 : 2}
              gap={16}
              showEmpty={false}
              renderItem={renderResult}
            />

            {error && (
              <div className="global-search__error" role="alert">
                <Text as="p" size="sm">
                  {error}
                </Text>
              </div>
            )}

            {hasMore && (
              <div className="global-search__footer">
                <Button
                  value={loadingMore ? "Loading..." : "Load More"}
                  variant="primary-outlined"
                  disabled={loading || loadingMore}
                  onClick={loadMore}
                />
              </div>
            )}
          </>
        )}
      </ContentPanel>
    </ViewContainer>
  );
}
